import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useInvoiceContext } from "../context";
import { API_BASE_URL } from "../config";
import { STATUS_WAITING, STATUS_PAID, STATUS_ERROR } from "../helpers";
import QRCode from "react-qr-code";
import Lottie from "lottie-react";
import animationPayed from "../assets/payed_animation.json";
import Confetti from "react-confetti";
import { ReactComponent as PixSVG } from "../assets/pix.svg";
import { faRightFromBracket, faWarning } from "@fortawesome/free-solid-svg-icons";
import ButtonCopy from "./ButtonCopy";
import { PAY_PIX } from "../helpers";
import showError from "./ModalError";

const ModalPayWithPIX = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [isPayed, setIsPayed] = useState(false);
  const { invoiceID, paymentMethod, setInvoiceID } = useInvoiceContext();
  const [errorString, setErrorString] = useState("");
  const [QRCodeString, setQRCodeString] = useState("");
  const [isProcessedWithError, setIsProcessedWithError] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 30; // Defina o número máximo de tentativas

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!invoiceID) return;
      if (isPayed) return;
      if (paymentMethod !== PAY_PIX) return;
      if (attempts >= MAX_ATTEMPTS) return;

      try {
        fetch(`${API_BASE_URL}/invoice/${invoiceID}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          console.log(response);
          if (!response.ok) {
            return response.json().then((errData) => {
              throw new Error(errData.message);
            });
          }
          return response.json();
        })
        .then((data) => {
          if (data.invoice.status === STATUS_WAITING) {
            setIsLoading(false);
            setIsReady(true);
            setQRCodeString(data.invoice.pix_key);
          } else if (data.invoice.status === STATUS_PAID && !isPayed) {
            setIsReady(false);
            setIsPayed(true);
          } else if (data.invoice.status === STATUS_ERROR) {
            setIsProcessedWithError(true);
            let history = data.invoice.historico; //    "historico": "ABERTA EM: 2024-04-09 10:46:31 | ERROR: credit card holder name invalid value: DWDDWDWDWD",
            let historyArr = history.split("|");
            let error = historyArr.find((element) => element.includes("ERROR"));
            console.log(error);
            if (error) {
              setIsLoading(false);
              setErrorString(error);
            }
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          showError(error);
        });

      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchInvoice();
    const interval = setInterval(() => {
      if (isPayed || isProcessedWithError) {
        clearInterval(interval);
        return;
      }
      setAttempts((prevAttempts) => prevAttempts + 1);
      fetchInvoice();
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [invoiceID, isPayed, attempts]);

  const closeModal = () => {
    setIsLoading(true);
    setIsReady(false);
    setIsPayed(false);
    setErrorString("");
    setQRCodeString("");
    setIsProcessedWithError(false);

    setInvoiceID("");

    props.onHide();
  }

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      backdrop="static"
      centered
    >
      <Confetti
        run={isPayed}
        numberOfPieces={800}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: "-1",
        }}
      />
      <Modal.Header>
        <PixSVG
          style={{
            fill: "#333",
            width: "18px",
            height: "18px",
          }}
          className="me-2"
        />
        <Modal.Title className="w-100">
          PIX
          {(
            isProcessedWithError &&
            !isPayed &&
            !isLoading
          )
          && (
            <Button
              variant="link"
              className="btn-close position-absolute top-0 end-0"
              style={{ marginTop: "1rem", marginRight: "1rem" }}
              onClick={closeModal}
            />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {isLoading && !isPayed && (
          <>
            <h4>Processando...</h4>
            <p className="mb-4">
              Aguarde enquanto estamos gerando o seu QRCODE para pagamento...
            </p>
            <div
              className="spinner-border text-success mb-4 fs-5"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </>
        )}

        {isReady && !isPayed && (
          <>
            <h4>Aguardando pagamento...</h4>
            <p className="mb-4">
              Abra o aplicativo do seu banco e aponte a câmera para o QRCode
            </p>
            <div>
              <p className="mb-4">
                <QRCode size={200} value={QRCodeString} />
              </p>
              <p className="mb-4">
                <small>
                  <strong>
                    Chave PIX copia e cola:
                    <ButtonCopy string={QRCodeString} />
                  </strong>
                  <p
                    className="text-muted text-break lh-1 mt-1"
                    style={{ fontSize: ".9em" }}
                  >
                    {QRCodeString}
                  </p>
                </small>
              </p>
            </div>
          </>
        )}

        {isPayed && (
          <>
            <h4>Pagamento efetuado com sucesso!</h4>
            <p>Obrigado por utilizar os nossos sistemas.</p>
            <Lottie
              animationData={animationPayed}
              loop={false}
              autoplay={true}
              style={{ height: "300px" }}
            />

            <p className="text-muted mb-1">
              <small>
                <FontAwesomeIcon
                  icon={faWarning}
                  className="me-2"
                  color="#dc3445"
                />
                <strong> Atenção:</strong> Sua licença e nota fiscal será
                enviada para o seu e-mail cadastrado.
              </small>
            </p>

            <Button
              variant="outline-success"
              className="mt-2"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "https://shoficina.com.br";
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ transform: "rotate(180deg)" }}>
                  <FontAwesomeIcon icon={faRightFromBracket} className="ms-2 " />
                </div>
                Voltar para o site
              </div>
            </Button>
          </>
        )}

        {isProcessedWithError && !isPayed && (
          <>
            <br></br>
            <h4>
              <FontAwesomeIcon icon={faWarning} color="#d9534f" className="me-2" />
              Ocorreu um erro ao processar o pagamento:
            </h4>
            <br></br><br></br>
            <p className="mb-4">
              {errorString}
            </p>
            <br></br>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ModalPayWithPIX;
